import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Radio,
  Globe2,
  Scissors,
  Coins,
  Music4,
  ShieldCheck,
  MessageCircle,
  ArrowRight,
  Sparkles,
  BadgeCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AuraGlobe } from "@/components/AuraGlobe";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SmartStoreBadges, StoreQRCodes } from "@/components/StoreBadges";
import { IMAGES } from "@/constants/site";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-70px" }}
    transition={{ delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const faqs = [
  { q: "What is AuraLive?", a: "AuraLive is a live-streaming social app where you go live from real places, discover creators on a world map, chat and send virtual gifts, and even make original music with AI." },
  { q: "How do coins and gifts work?", a: "Coins are AuraLive's in-app currency. Buy coins via the App Store, Google Play or on the web, then send animated gifts and tips to creators during their live streams. Creators earn from gifts and can cash out once eligible." },
  { q: "What is the AI Music Studio?", a: "AuraLive's Music Lab lets you create an original song in your language and style in about a minute — pick from 10 languages and 12 genres (Pop, Anadolu Pop, Amapiano, K-pop, Arabesk and more), write your own lyrics or generate them with AI, add AI cover art, then post it or go live with your sound. New creators get 500 welcome coins and 2 free songs a day to start." },
  { q: "Is my exact location shared?", a: "Your precise location is only used while you are actively broadcasting so viewers can find your stream. When you go offline, only a coarse, city-level area may remain for discovery. You can disable location at the OS level anytime." },
  { q: "Is AuraLive safe?", a: "Yes. Going live requires identity verification, and every stream is covered by real-time AI moderation plus report and block tools — so the community stays authentic and respectful." },
];

const MUSIC_LANGUAGES = ["Turkish", "English", "Spanish", "Arabic", "Hindi", "Portuguese", "Korean", "Japanese", "French", "German"];
const MUSIC_GENRES = ["Pop", "Anadolu Pop", "Hip-Hop", "Afrobeats", "Amapiano", "Reggaeton", "R&B", "Rock", "EDM", "Lo-fi", "Arabesk", "K-pop"];
const MUSIC_FLOW = [
  { n: "01", t: "Name & language", d: "Title your track and pick from 10 languages." },
  { n: "02", t: "Choose a genre", d: "12 styles — Pop, Anadolu Pop, Amapiano, K-pop, Arabesk…" },
  { n: "03", t: "Describe the vibe", d: "e.g. “summer night in Istanbul, longing for someone.”" },
  { n: "04", t: "Lyrics your way", d: "Write your own, tap “Write with AI”, or go instrumental." },
  { n: "05", t: "Generate cover art", d: "A unique AI album cover — regenerate until you love it." },
  { n: "06", t: "Compose in ~1 min", d: "Real sung vocals via the ElevenLabs music engine." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 pt-32 md:px-12 md:pt-40">
        <div className="mx-auto grid max-w-7xl items-center gap-10 pb-16 md:pb-24 lg:grid-cols-[1.05fr_0.95fr]">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-aura-pink">
              See anywhere. Connect everywhere.
            </p>
            <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-aura-ink sm:text-5xl sm:tracking-tighter md:text-6xl lg:text-7xl">
              Go live from <span className="grad-text">real places.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-aura-text2 md:text-lg">
              Discover creators on a living world map, drop into their stream,
              send gifts in real time, and make original music with AI. The
              planet, live — in your pocket.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/#download"
                data-testid="hero-get-app"
                onClick={() => { const el = document.getElementById("download"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
                className="group inline-flex items-center gap-2 rounded-full bg-aura-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-aura-ink2 hover:-translate-y-0.5"
              >
                Download app
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/#features"
                data-testid="hero-learn-more"
                className="inline-flex items-center gap-2 rounded-full border border-black/15 bg-white px-7 py-3.5 text-sm font-semibold text-aura-ink transition-all hover:border-black/40"
              >
                Explore features
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-xs font-medium text-aura-text2">
              <ShieldCheck size={14} className="text-aura-pink" />
              Verified creators · Real moments · From your city to the world
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.1 }} className="relative min-w-0">
            <ErrorBoundary
              fallback={
                <div className="relative flex w-full items-center justify-center">
                  <div className="aspect-square w-[74%] max-w-[420px] rounded-full opacity-90 shadow-2xl" style={{ background: "var(--grad-brand)" }} />
                </div>
              }
            >
              <AuraGlobe />
            </ErrorBoundary>
            {/* floating pill badges */}
            <div className="absolute left-2 top-4 z-20 hidden animate-floaty items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-aura-ink shadow-lg sm:flex">
              <span className="h-2 w-2 rounded-full bg-aura-pink" /> 2,431 live now
            </div>
            <div className="absolute bottom-10 right-0 z-20 hidden animate-floaty items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-semibold text-aura-ink shadow-lg sm:flex" style={{ animationDelay: "1.5s" }}>
              <Coins size={14} className="text-aura-violet" /> +1,240 coins gifted
            </div>
          </motion.div>
        </div>
      </section>

      {/* POWERED-BY STRIP */}
      <div className="border-y border-black/[0.06] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-6 py-6 md:flex-row md:justify-between md:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-muted">
            Built on best-in-class infrastructure
          </p>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm font-semibold text-aura-text2">
            <span>LiveKit</span><span>ElevenLabs</span><span>Stripe</span><span>RevenueCat</span><span>Object Storage</span>
          </div>
        </div>
      </div>

      {/* FEATURES BENTO */}
      <section id="features" className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">Everything, live</p>
            <h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight text-aura-ink md:text-5xl">
              A whole live world, built into one app.
            </h2>
          </Reveal>

          <div className="mt-14 grid auto-rows-[minmax(0,1fr)] gap-6 md:grid-cols-3 md:gap-8">
            {/* Big feature: Live Rooms with image */}
            <Reveal className="md:col-span-2 md:row-span-2">
              <div className="group relative flex h-full min-h-[420px] flex-col justify-end overflow-hidden rounded-3xl border border-black/[0.06] bg-aura-ink p-8 text-white transition-transform hover:-translate-y-1">
                <img src={IMAGES.live} alt="Going live" className="absolute inset-0 h-full w-full object-cover opacity-55 transition-opacity group-hover:opacity-65" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="relative">
                  <span className="inline-flex items-center gap-2 rounded-full bg-aura-pink px-3 py-1 text-xs font-bold">
                    <span className="h-1.5 w-1.5 rounded-full bg-white" /> LIVE ROOMS
                  </span>
                  <h3 className="mt-4 font-heading text-3xl font-semibold tracking-tight">Real-time video, chat, reactions & gifts</h3>
                  <p className="mt-2 max-w-lg text-sm leading-6 text-white/80">
                    Broadcast in HD with LiveKit. Viewers chat, react and shower
                    you with animated gifts — every moment, as it happens.
                  </p>
                </div>
              </div>
            </Reveal>

            {[
              { icon: Globe2, t: "World-map discovery", d: "Find creators live near you and across the planet on an interactive map, plus a trending feed." },
              { icon: Scissors, t: "Clips & highlights", d: "Replays auto-cut into shareable vertical clips — your best moments, ready to go viral." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.06}>
                <div className="group h-full rounded-3xl border border-black/[0.06] bg-white p-8 transition-all hover:-translate-y-1 hover:border-black/20 solid-card">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl grad-bg text-white">
                    <f.icon size={22} />
                  </span>
                  <h3 className="mt-6 font-heading text-2xl font-medium tracking-tight text-aura-ink">{f.t}</h3>
                  <p className="mt-2 text-sm leading-6 text-aura-text2">{f.d}</p>
                </div>
              </Reveal>
            ))}

            {[
              { icon: Coins, t: "Coin economy", d: "Wallet, gift catalog and creator earnings. Buy coins on web or mobile; creators cash out." },
              { icon: MessageCircle, t: "Profiles, follows & DMs", d: "Viewer and creator profiles, followers, creator suggestions, notifications and private messaging." },
              { icon: BadgeCheck, t: "Verify to go live", d: "Stripe Identity verification, AI moderation, report & block — a community you can trust." },
            ].map((f, i) => (
              <Reveal key={f.t} delay={i * 0.06}>
                <div className="group h-full rounded-3xl border border-black/[0.06] bg-white p-8 transition-all hover:-translate-y-1 hover:border-black/20 solid-card">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl border border-black/10 bg-aura-base text-aura-ink">
                    <f.icon size={22} />
                  </span>
                  <h3 className="mt-6 font-heading text-2xl font-medium tracking-tight text-aura-ink">{f.t}</h3>
                  <p className="mt-2 text-sm leading-6 text-aura-text2">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI MUSIC STUDIO — inverted dark, featured */}
      <section id="music" className="relative overflow-hidden bg-aura-ink px-6 py-24 text-white md:px-12 md:py-32">
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full opacity-40 blur-[130px]" style={{ background: "var(--grad-brand)" }} />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full opacity-30 blur-[130px]" style={{ background: "var(--grad-brand)" }} />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] grad-text">
              <Music4 size={14} className="text-aura-pink" /> AI Music Studio · Music Lab
            </p>
            <h2 className="mt-4 max-w-3xl font-heading text-4xl font-semibold tracking-tight md:text-6xl">
              Make a hit in your language. <span className="grad-text">Go live with it.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
              Create your own original song — your language, your style — in about
              a minute. Then post it, go live with your sound, or let others tap
              "use this sound" and carry your track across the world.
            </p>
          </Reveal>

          {/* promo stats */}
          <Reveal delay={0.05}>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {[
                { k: "10", t: "languages", s: "Turkish to Korean" },
                { k: "12", t: "genres", s: "Anadolu Pop to Amapiano" },
                { k: "~60s", t: "to a finished song", s: "vocals, lyrics & cover art" },
              ].map((x) => (
                <div key={x.t} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6">
                  <p className="font-heading text-4xl font-semibold grad-text">{x.k}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{x.t}</p>
                  <p className="text-xs text-white/60">{x.s}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-8 grid items-start gap-8 lg:grid-cols-2">
            {/* creation flow */}
            <Reveal>
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">From idea to live in 60 seconds</p>
                <ol className="mt-6 space-y-5">
                  {MUSIC_FLOW.map((s) => (
                    <li key={s.n} className="flex gap-4">
                      <span className="font-heading text-sm font-bold grad-text">{s.n}</span>
                      <div>
                        <p className="font-semibold text-white">{s.t}</p>
                        <p className="text-sm text-white/60">{s.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            {/* image + chips */}
            <Reveal delay={0.1}>
              <div className="grad-border relative overflow-hidden rounded-3xl">
                <img src={IMAGES.music} alt="AI music studio" className="h-[280px] w-full object-cover md:h-[320px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-aura-ink via-aura-ink/40 to-transparent" />
                <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
                  <span className="grid h-9 w-9 place-items-center rounded-full grad-bg"><Music4 size={16} /></span>
                  <div className="leading-tight">
                    <p className="text-sm font-semibold text-white">Sound of the Week</p>
                    <p className="text-[11px] text-white/70">Your city's top tracks — then the world</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-white/50">Sing in your language</p>
                  <div className="flex flex-wrap gap-2">
                    {MUSIC_LANGUAGES.map((l) => (
                      <span key={l} className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-medium text-white/80">{l}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-white/50">Pick your genre</p>
                  <div className="flex flex-wrap gap-2">
                    {MUSIC_GENRES.map((g) => (
                      <span key={g} className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-xs font-medium text-white/80">{g}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.05}>
            <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:flex-row md:items-center">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
                <span className="inline-flex items-center gap-2"><Sparkles size={14} className="text-aura-pink" /> Share, go live, or "use this sound"</span>
                <span className="inline-flex items-center gap-2"><Music4 size={14} className="text-aura-violet" /> Trending Sounds, city-first</span>
              </div>
              <p className="text-sm font-semibold grad-text">500 welcome coins · 2 free songs a day</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST & SAFETY */}
      <section className="px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">Trust & safety</p>
            <h2 className="mt-4 max-w-2xl font-heading text-4xl font-medium tracking-tight text-aura-ink md:text-5xl">
              A serious platform for a live world.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { icon: BadgeCheck, t: "Identity verified", d: "Creators verify with Stripe Identity before they can go live — real people, accountable streams." },
              { icon: ShieldCheck, t: "AI moderation", d: "Real-time AI text moderation plus report & block tools. Explicit content is never allowed." },
              { icon: Globe2, t: "Privacy by design", d: "Coarse offline location, privacy-hardened DMs and controls engineered in from day one." },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 0.06}>
                <div className="border-t-2 border-aura-ink pt-6">
                  <x.icon size={24} className="text-aura-ink" />
                  <h3 className="mt-4 font-heading text-2xl font-medium tracking-tight text-aura-ink">{x.t}</h3>
                  <p className="mt-2 text-sm leading-6 text-aura-text2">{x.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/safety" data-testid="safety-link" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-aura-pink transition-colors hover:text-aura-pinkHover">
              Read our Community Guidelines <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section id="download" className="px-6 pb-28 md:px-12">
        <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-black/[0.06] bg-white px-8 py-16 text-center md:px-16 md:py-24">
          <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full opacity-25 blur-[110px]" style={{ background: "var(--grad-brand)" }} />
          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">
              Out now on iPhone &amp; Android
            </span>
            <h2 className="mx-auto mt-5 max-w-3xl font-heading text-4xl font-semibold tracking-tighter text-aura-ink md:text-6xl">
              The world is going <span className="grad-text">live.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-aura-text2">
              AuraLive is available now on the Apple App Store and Google Play.
              Download it, go live from anywhere, and start your first sound.
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-8 md:flex-row md:items-center">
              {/* Store badges — matching platform surfaced first */}
              <SmartStoreBadges idPrefix="download" />

              {/* QR codes — scan to open either store */}
              <div className="flex flex-col items-center gap-4 rounded-2xl border border-black/[0.06] bg-white p-5 solid-card sm:flex-row" data-testid="download-qr-card">
                <StoreQRCodes testIdPrefix="download" size={88} />
                <div className="max-w-[10rem] text-center sm:text-left">
                  <p className="font-heading text-sm font-semibold text-aura-ink">Scan to download</p>
                  <p className="mt-1 text-xs leading-5 text-aura-text2">Point your phone camera at the App Store or Google Play code.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 pb-28 md:px-12">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aura-pink">FAQ</p>
            <h2 className="mt-4 font-heading text-4xl font-medium tracking-tight text-aura-ink md:text-5xl">
              Questions, answered.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-black/10">
                  <AccordionTrigger className="py-5 text-left font-heading text-lg font-medium text-aura-ink hover:text-aura-pink hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-7 text-aura-text2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
